import { useCallback, useEffect, useRef, useState } from "react";

import API_BASE, { withApiCredentials } from "../api";
import { getActiveAwareAuthToken } from "../auth/bootstrapTokens";

export const USER_POSTS_PAGE_LIMIT = 20;

export function sortPostsNewestFirst(posts) {
  return [...posts].sort((left, right) => {
    const dateDifference = Number(right?.postDate || 0) - Number(left?.postDate || 0);
    if (dateDifference !== 0) return dateDifference;
    return String(left?.id || "").localeCompare(String(right?.id || ""));
  });
}

export function mergePostsById(existingPosts, incomingPosts, { incomingWins = true } = {}) {
  const postsById = new Map();
  const addPost = (post, replaceExisting) => {
    const postId = String(post?.id || "").trim();
    if (!postId) return;
    if (!postsById.has(postId) || replaceExisting) {
      postsById.set(postId, post);
    }
  };

  existingPosts.forEach((post) => addPost(post, false));
  incomingPosts.forEach((post) => addPost(post, incomingWins));

  return sortPostsNewestFirst(Array.from(postsById.values()));
}

function normalizePostPage(posts) {
  if (!Array.isArray(posts)) return [];
  return posts.map((post) => ({
    ...post,
    likes: Array.isArray(post?.likes) ? post.likes : [],
    comments: Array.isArray(post?.comments) ? post.comments : [],
  }));
}

export default function usePaginatedUserPosts(userId) {
  const normalizedUserId = String(userId || "").trim();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(Boolean(normalizedUserId));
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(Boolean(normalizedUserId));
  const generationRef = useRef(0);
  const nextOffsetRef = useRef(0);
  const serverCountedPostIdsRef = useRef(new Set());
  const requestInFlightRef = useRef(false);
  const abortControllerRef = useRef(null);

  const requestPage = useCallback(
    async ({
      offset,
      replace,
      preservePagination = false,
      incomingWins = replace,
    }) => {
      if (!normalizedUserId || requestInFlightRef.current) return false;

      const generation = generationRef.current;
      const controller = new AbortController();
      abortControllerRef.current = controller;
      requestInFlightRef.current = true;
      if (replace) setLoading(true);
      else setLoadingMore(true);

      try {
        const query = new URLSearchParams({
          limit: String(USER_POSTS_PAGE_LIMIT),
          offset: String(offset),
        });
        const token = getActiveAwareAuthToken(window);
        const response = await fetch(
          `${API_BASE}/users/${encodeURIComponent(normalizedUserId)}/posts?${query}`,
          withApiCredentials({
            signal: controller.signal,
            ...(token
              ? { headers: { Authorization: `Bearer ${token}` } }
              : {}),
          })
        );
        if (!response.ok) throw new Error(`User posts request failed (${response.status})`);

        const page = normalizePostPage(await response.json());
        if (controller.signal.aborted || generationRef.current !== generation) {
          return false;
        }

        setPosts((current) =>
          replace
            ? mergePostsById([], page)
            : mergePostsById(current, page, { incomingWins })
        );
        if (!preservePagination) {
          page.forEach((post) => serverCountedPostIdsRef.current.add(post.id));
          nextOffsetRef.current = offset + page.length;
          setHasMore(page.length === USER_POSTS_PAGE_LIMIT);
        } else {
          const newServerPostIds = page
            .map((post) => String(post?.id || "").trim())
            .filter(
              (postId) =>
                postId && !serverCountedPostIdsRef.current.has(postId)
            );
          newServerPostIds.forEach((postId) =>
            serverCountedPostIdsRef.current.add(postId)
          );
          nextOffsetRef.current += newServerPostIds.length;
        }
        return true;
      } catch (error) {
        if (error?.name !== "AbortError") {
          console.error("Failed to load user posts:", error);
          setHasMore(false);
        }
        return false;
      } finally {
        if (generationRef.current === generation) {
          requestInFlightRef.current = false;
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [normalizedUserId]
  );

  useEffect(() => {
    generationRef.current += 1;
    abortControllerRef.current?.abort();
    requestInFlightRef.current = false;
    nextOffsetRef.current = 0;
    serverCountedPostIdsRef.current.clear();
    setPosts([]);
    setLoading(Boolean(normalizedUserId));
    setLoadingMore(false);
    setHasMore(Boolean(normalizedUserId));

    if (normalizedUserId) {
      requestPage({ offset: 0, replace: true });
    }

    return () => abortControllerRef.current?.abort();
  }, [normalizedUserId, requestPage]);

  const loadMore = useCallback(() => {
    if (!normalizedUserId || !hasMore || loading || loadingMore) return false;
    return requestPage({ offset: nextOffsetRef.current, replace: false });
  }, [hasMore, loading, loadingMore, normalizedUserId, requestPage]);

  const refresh = useCallback(() => {
    if (!normalizedUserId || requestInFlightRef.current) return false;

    return requestPage({
      offset: 0,
      replace: false,
      preservePagination: true,
      incomingWins: true,
    });
  }, [normalizedUserId, requestPage]);

  const updatePost = useCallback((updatedPost) => {
    if (!updatedPost?.id) return;
    setPosts((current) =>
      current.map((post) =>
        post?.id === updatedPost.id
          ? {
              ...post,
              ...updatedPost,
              catch: updatedPost.catch
                ? { ...(post.catch || {}), ...updatedPost.catch }
                : post.catch,
            }
          : post
      )
    );
  }, []);

  const removePost = useCallback((postId) => {
    setPosts((current) => current.filter((post) => post?.id !== postId));
    if (serverCountedPostIdsRef.current.delete(String(postId))) {
      nextOffsetRef.current = Math.max(0, nextOffsetRef.current - 1);
    }
  }, []);

  useEffect(() => {
    const updateMatchingPost = (postId, updater) => {
      setPosts((current) =>
        current.map((post) => (post?.id === postId ? updater(post) : post))
      );
    };
    const handlePostLiked = (event) => {
      const { postId, liked, likesCount, actorUserId } = event.detail || {};
      updateMatchingPost(postId, (post) => {
        const actorId = String(actorUserId || "");
        const otherLikes = (Array.isArray(post.likes) ? post.likes : []).filter(
          (likeUserId) => String(likeUserId) !== actorId
        );
        return {
          ...post,
          likes: liked && actorId
            ? [...otherLikes.slice(0, Math.max(0, Number(likesCount) - 1)), actorId]
            : otherLikes.slice(0, Math.max(0, Number(likesCount) || 0)),
        };
      });
    };
    const handlePostCommented = (event) => {
      const { postId, comment } = event.detail || {};
      updateMatchingPost(postId, (post) => ({
        ...post,
        comments: [...(post.comments || []), comment],
      }));
    };
    const handleCommentLiked = (event) => {
      const { postId, commentId, likes } = event.detail || {};
      updateMatchingPost(postId, (post) => ({
        ...post,
        comments: (post.comments || []).map((comment) =>
          comment.id === commentId ? { ...comment, likes } : comment
        ),
      }));
    };
    const handleCommentReplied = (event) => {
      const { postId, commentId, reply } = event.detail || {};
      updateMatchingPost(postId, (post) => ({
        ...post,
        comments: (post.comments || []).map((comment) =>
          comment.id === commentId
            ? { ...comment, replies: [...(comment.replies || []), reply] }
            : comment
        ),
      }));
    };
    const handleReplyLiked = (event) => {
      const { postId, commentId, replyId, likes } = event.detail || {};
      updateMatchingPost(postId, (post) => ({
        ...post,
        comments: (post.comments || []).map((comment) =>
          comment.id === commentId
            ? {
                ...comment,
                replies: (comment.replies || []).map((reply) =>
                  reply.id === replyId ? { ...reply, likes } : reply
                ),
              }
            : comment
        ),
      }));
    };

    window.addEventListener("postLiked", handlePostLiked);
    window.addEventListener("postCommented", handlePostCommented);
    window.addEventListener("commentLiked", handleCommentLiked);
    window.addEventListener("commentReplied", handleCommentReplied);
    window.addEventListener("replyLiked", handleReplyLiked);
    window.addEventListener("postsUpdated", refresh);
    return () => {
      window.removeEventListener("postLiked", handlePostLiked);
      window.removeEventListener("postCommented", handlePostCommented);
      window.removeEventListener("commentLiked", handleCommentLiked);
      window.removeEventListener("commentReplied", handleCommentReplied);
      window.removeEventListener("replyLiked", handleReplyLiked);
      window.removeEventListener("postsUpdated", refresh);
    };
  }, [refresh]);

  return {
    posts,
    loading,
    loadingMore,
    hasMore,
    loadMore,
    refresh,
    updatePost,
    removePost,
  };
}
